// YTRepliesRateLimitsPair — Jul 30, 2026 (Randy pt89c-xxxviii)
//
// Wraps two `YTRepliesRateLimitsCard`s into ONE mobile-safe container with
// channel-tab chooser + SHARED collapse/expand chevron. Mirrors the design
// of YTRepliesConsolePair.
//
// Hidden entirely (returns null) when the user has zero entitlement on
// yt_ai_replies — matches Randy's spec: "or you can just make the bottom
// section (rate limits) hidden when it's in this state".

import { useState } from "react";
import { Gauge, LockKey } from "@phosphor-icons/react";
import { useEntitlements } from "@/lib/entitlements";
import YTRepliesRateLimitsCard from "@/components/YTRepliesRateLimitsCard";

export default function YTRepliesRateLimitsPair({ siloId }) {
  const { owns } = useEntitlements();
  const isUnlocked = owns("yt_ai_replies");
  const channel1Entitled = isUnlocked;
  const channel2Entitled = isUnlocked;

  const [activeChannel, setActiveChannel] = useState("channel1");
  const [collapsed, setCollapsed] = useState(true); // matches individual card default

  // Both unentitled → hide the whole rate-limits block per Randy's spec.
  if (!channel1Entitled && !channel2Entitled) {
    return null;
  }

  const renderTabButton = (slot, label) => {
    const entitled = slot === "channel1" ? channel1Entitled : channel2Entitled;
    const isActive = activeChannel === slot;
    return (
      <button
        key={slot}
        type="button"
        onClick={() => entitled && setActiveChannel(slot)}
        disabled={!entitled}
        data-testid={`yt-rate-limits-pair-tab-${slot}`}
        /* Jun 2026 (Randy) — MOBILE: same squeeze as the console pair. Two
           tabs plus a collapse control in one 390px row truncated the labels
           to "RATE LI… / Auto-pos…". Stacks below `sm`, unchanged above. */
        className={`w-full sm:flex-1 min-w-0 flex items-center gap-2 px-4 py-3 border-b sm:border-b-0 sm:border-r hairline last:border-b-0 sm:last:border-r-0 transition ${
          isActive
            ? "bg-white shadow-[inset_3px_0_0_#4A5D4E] sm:shadow-none"
            : entitled
              ? "bg-[#F4F3EE]/40 hover:bg-white/60"
              : "bg-[#F4F3EE]/40 opacity-60 cursor-not-allowed"
        }`}
      >
        {entitled ? (
          <Gauge size={14} weight="duotone" className="text-[#4A5D4E] shrink-0" />
        ) : (
          <LockKey size={11} weight="duotone" className="text-[#6B6A68] shrink-0" />
        )}
        <div className="text-left min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] font-medium truncate">
            Rate Limits · {label}
          </div>
          <div className="text-[12px] font-medium text-[#1C1B1A] truncate">
            {entitled ? "Auto-post thresholds for this channel" : "Locked (upgrade required)"}
          </div>
        </div>
      </button>
    );
  };

  return (
    <div
      className="mt-3 border hairline rounded-2xl bg-[#FDFCF8] shadow-sm overflow-hidden"
      data-testid="yt-rate-limits-pair"
    >
      <div className="flex flex-col sm:flex-row sm:items-stretch border-b hairline bg-white/40">
        {renderTabButton("channel1", "Channel 1")}
        {renderTabButton("channel2", "Channel 2")}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          data-testid="yt-rate-limits-pair-collapse"
          className="w-full sm:w-auto text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E] hover:text-[#1C1B1A] px-4 py-2.5 sm:py-1 border-t sm:border-t-0 sm:border-l hairline"
        >
          {collapsed ? "Expand ▾" : "Collapse ▴"}
        </button>
      </div>

      {!collapsed && (
        <>
          <div style={{ display: activeChannel === "channel1" ? "block" : "none" }}>
            <YTRepliesRateLimitsCard siloId={siloId} channelSlot="channel1" pairMode />
          </div>
          <div style={{ display: activeChannel === "channel2" ? "block" : "none" }}>
            <YTRepliesRateLimitsCard siloId={siloId} channelSlot="channel2" pairMode />
          </div>
        </>
      )}
    </div>
  );
}
